/**
 * Home Page Navigation Map
 * Defines all focusable elements and their spatial relationships on the home page
 */

import type { PageNavigationMap } from '../tvNavigationMap';

/**
 * Base home page navigation map
 * Dynamic elements (rows and cards) are added at runtime
 */
export const homePageMap: PageNavigationMap = {
  pageId: 'home',
  zones: {
    header: {
      nodes: [
        {
          id: 'logo',
          type: 'button',
          component: 'Header',
          position: { zone: 'header', columnIndex: 0 },
          neighbors: {
            right: 'search-input',
            down: 'hero-play-button'
          }
        },
        {
          id: 'search-input',
          type: 'input',
          component: 'Header',
          position: { zone: 'header', columnIndex: 1 },
          neighbors: {
            left: 'logo',
            right: 'profile-button',
            down: 'hero-play-button'
          }
        },
        {
          id: 'profile-button',
          type: 'button',
          component: 'Header',
          position: { zone: 'header', columnIndex: 2 },
          neighbors: {
            left: 'search-input',
            down: 'hero-play-button'
          }
        }
      ],
      defaultFocus: 'logo'
    },
    hero: {
      nodes: [
        {
          id: 'hero-play-button',
          type: 'button',
          component: 'HeroBillboard',
          position: { zone: 'hero', columnIndex: 0 },
          neighbors: {
            up: 'logo',
            right: 'hero-info-button',
            down: 'continue-watching-card-0'  // Will be set dynamically
          }
        },
        {
          id: 'hero-info-button',
          type: 'button',
          component: 'HeroBillboard',
          position: { zone: 'hero', columnIndex: 1 },
          neighbors: {
            up: 'logo',
            left: 'hero-play-button',
            down: 'continue-watching-card-0'  // Will be set dynamically
          }
        }
      ]
    },
    content: {
      nodes: [
        // Row nodes will be added dynamically when rows mount
        // Card nodes will be added dynamically when cards render
      ]
    }
  }
};

/**
 * Row order for home page
 * This defines the sequence of rows and is used to calculate neighbors
 */
export const HOME_PAGE_ROW_ORDER = [
  'continue-watching',
  'trending',
  'popular',
  'browse-by-language',
  'browse-by-genre'
] as const;

/**
 * Get row ID for a given row index
 */
export function getRowIdForIndex(index: number): string | undefined {
  return HOME_PAGE_ROW_ORDER[index];
}

/**
 * Get next row ID for a given row ID
 */
export function getNextRowId(currentRowId: string): string | undefined {
  const index = HOME_PAGE_ROW_ORDER.indexOf(currentRowId as any);
  if (index >= 0 && index < HOME_PAGE_ROW_ORDER.length - 1) {
    return HOME_PAGE_ROW_ORDER[index + 1];
  }
  return undefined;
}

/**
 * Get previous row ID for a given row ID
 */
export function getPreviousRowId(currentRowId: string): string | undefined {
  const index = HOME_PAGE_ROW_ORDER.indexOf(currentRowId as any);
  if (index > 0) {
    return HOME_PAGE_ROW_ORDER[index - 1];
  }
  return undefined;
}


